import React from 'react';
import { SharedLink } from './LinkSharing';

interface LinkListProps {
  links: SharedLink[];
  loading: boolean;
  onLikeLink: (linkId: number) => void;
}

export const LinkList: React.FC<LinkListProps> = ({ links, loading, onLikeLink }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryEmoji = (category: string) => {
    const emojiMap: { [key: string]: string } = {
      course: '🎓',
      tutorial: '📚',
      article: '📝',
      tool: '🛠️',
      documentation: '📖',
      video: '🎥',
      podcast: '🎧',
      book: '📚',
      research: '🔬',
      general: '🔗'
    };
    return emojiMap[category] || '🔗';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🔗</div>
        <h3 className="text-xl font-semibold text-gray-600 mb-2">No links found</h3>
        <p className="text-gray-500">Be the first to share a valuable resource!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {links.map((link) => (
        <div 
          key={link.id} 
          className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 ${
            link.is_featured ? 'ring-2 ring-yellow-400' : ''
          }`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{getCategoryEmoji(link.category)}</span>
                <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                  {link.category}
                </span>
                {link.is_featured && (
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-semibold">
                    ⭐ Featured
                  </span>
                )}
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                <a 
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-blue-600 transition-colors duration-200"
                >
                  {link.title}
                </a>
              </h3>
              
              {link.description && (
                <p className="text-gray-600 mb-3 line-clamp-2">
                  {link.description}
                </p>
              )}
              
              {link.tags && link.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {link.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>
                  👤 {link.user_name || 'Anonymous'}
                </span>
                <span>
                  📅 {formatDate(link.created_at)}
                </span>
                <span>
                  👀 {link.views} views
                </span>
              </div>
            </div>
            
            <div className="flex flex-col items-center ml-4">
              <button
                onClick={() => onLikeLink(link.id)}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
              >
                <span className="text-red-500">❤️</span>
                <span className="font-semibold">{link.likes}</span>
              </button>
              
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200"
              >
                Visit →
              </a>
            </div>
          </div>
          
          <div className="border-t pt-3">
            <div className="flex justify-between items-center text-xs text-gray-400">
              <span>
                {new URL(link.url).hostname}
              </span>
              <span>
                ID: {link.id}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};